import { cellToLatLng, greatCircleDistance, UNITS } from "h3-js";

export type RequestState =
  | "open" | "in_progress" | "en_route" | "arrived" | "partial" | "done" | "cancelled" | "reopened";

export interface SealedBroadcastRef { ciphertext: { $bytes: string }; keyRef: string }
export interface SealedNeedToKnowRef { ciphertext: { $bytes: string }; recipients: string[] }

export interface OfferView {
  offerId: string;
  requestId: string;
  helper: string;
  tags: string[];
  etaMinutes?: number;
  note?: SealedBroadcastRef;
  createdAtIso: string;
  seq: number;
  /** Folded locally at publish time and not yet seen on the firehose. */
  pending?: boolean;
}

export interface NoteView {
  noteId: string;
  requestId: string;
  author: string;
  body: SealedNeedToKnowRef;
  createdAtIso: string;
  seq: number;
}

export interface HelpRequestView {
  requestId: string;
  creator: string;
  need: string;
  headline?: SealedBroadcastRef;
  details?: SealedBroadcastRef;
  urgency: number;
  people?: number;
  state: RequestState;
  cell: string;
  createdAtIso: string;
  lastSeq: number;
  offers: OfferView[];
  acceptedHelpers: string[];
  /** offerId chosen on accept (the locationGrant rode that accept event). */
  acceptedOfferId?: string;
  /** need-to-know locationGrant from the accept event (only the accepted helper can open it). */
  locationGrant?: SealedNeedToKnowRef;
  notes: NoteView[];
  /** Folded locally at publish time and not yet seen on the firehose. */
  pending?: boolean;
}

/** A locally folded event awaiting its firehose echo (persisted in the browser store). */
export interface PendingEntry { id: string; json: Record<string, unknown>; atMs: number }

/** Pending entries older than this are dropped on restore (the publish evidently never landed). */
export const PENDING_MAX_AGE_MS = 24 * 3_600_000;

const TERMINAL: ReadonlySet<RequestState> = new Set<RequestState>(["done", "cancelled"]);

/** Latest-state-wins fold over the help firehose, keyed by requestId (= create event id).
 *  Pure over event JSON — no clock reads. Sealed fields stay ciphertext (screens open them). */
export class HelpStore {
  #byId = new Map<string, HelpRequestView>();
  #stateSeq = new Map<string, number>();
  /** Locally folded events not yet echoed by the relay, by event id. */
  #pending = new Map<string, PendingEntry>();
  constructor(private me: string) {}

  /** Fold one event. `pending: true` = our own publish folded locally before the firehose
   *  echoes it (shown tinted); the echo carries the same id and clears the mark. */
  ingest(evt: { seq: number; json: Record<string, unknown>; pending?: boolean; atMs?: number }): void {
    const json = evt.json ?? {};
    const evId = json.id as string | undefined;
    if (evt.pending && evId) this.#pending.set(evId, { id: evId, json, atMs: evt.atMs ?? 0 });
    else if (evId && this.#pending.has(evId)) this.#pending.delete(evId);
    const type = json["$type"] as string;
    const who = json.identityId as string;
    const createdAtIso = (json.createdAt as string) ?? "";
    const geo = (json.geo ?? {}) as { cell?: string };
    const p = (json.payload ?? {}) as Record<string, unknown>;
    const subject = json.subject as string | undefined;
    const id = json.id as string;

    switch (type) {
      case "app.aid.helpRequest.create": {
        const existing = this.#byId.get(id);
        if (existing) {
          if (existing.pending && !evt.pending) { // the echo of our own create
            delete existing.pending;
            existing.lastSeq = Math.max(existing.lastSeq, evt.seq);
            this.#stateSeq.set(id, Math.max(this.#stateSeq.get(id) ?? 0, evt.seq));
          }
          return;
        }
        this.#byId.set(id, {
          ...(evt.pending ? { pending: true } : {}),
          requestId: id, creator: who, need: p.need as string,
          headline: p.headline as SealedBroadcastRef | undefined,
          details: p.details as SealedBroadcastRef | undefined,
          urgency: (p.urgency as number) ?? 1, people: p.people as number | undefined,
          state: "open", cell: geo.cell ?? "", createdAtIso,
          lastSeq: evt.seq,
          offers: [], acceptedHelpers: [], notes: [],
        });
        this.#stateSeq.set(id, evt.seq);
        return;
      }
      case "app.aid.helpRequest.amend": {
        const r = this.#byId.get(subject ?? ""); if (!r) return;
        r.lastSeq = Math.max(r.lastSeq, evt.seq);
        if (p.need !== undefined) r.need = p.need as string;
        if (p.headline !== undefined) r.headline = p.headline as SealedBroadcastRef;
        if (p.details !== undefined) r.details = p.details as SealedBroadcastRef;
        if (p.urgency !== undefined) r.urgency = p.urgency as number;
        if (p.people !== undefined) r.people = p.people as number;
        return;
      }
      case "app.aid.helpRequest.offer": {
        const r = this.#byId.get(subject ?? ""); if (!r) return;
        r.lastSeq = Math.max(r.lastSeq, evt.seq);
        const known = r.offers.find((o) => o.offerId === id);
        if (known) {
          if (known.pending && !evt.pending) { delete known.pending; known.seq = evt.seq; } // the echo
          return;
        }
        r.offers.push({
          offerId: id, requestId: r.requestId, helper: who,
          tags: (p.tags as string[]) ?? [], etaMinutes: p.etaMinutes as number | undefined,
          note: p.note as SealedBroadcastRef | undefined, createdAtIso, seq: evt.seq,
          ...(evt.pending ? { pending: true } : {}),
        });
        return;
      }
      case "app.aid.helpRequest.accept": {
        const r = this.#byId.get(subject ?? ""); if (!r) return;
        r.lastSeq = Math.max(r.lastSeq, evt.seq);
        const offerId = p.offer as string;
        const offer = r.offers.find((o) => o.offerId === offerId);
        r.acceptedOfferId = offerId;
        if (offer && !r.acceptedHelpers.includes(offer.helper)) r.acceptedHelpers.push(offer.helper);
        if (p.locationGrant !== undefined) r.locationGrant = p.locationGrant as SealedNeedToKnowRef;
        this.#applyState(r.requestId, "in_progress", evt.seq);
        return;
      }
      case "app.aid.helpRequest.transition": {
        const r = this.#byId.get(subject ?? ""); if (!r) return;
        r.lastSeq = Math.max(r.lastSeq, evt.seq);
        this.#applyState(r.requestId, p.to as RequestState, evt.seq);
        return;
      }
      case "app.aid.helpRequest.note": {
        const r = this.#byId.get(subject ?? ""); if (!r) return;
        r.lastSeq = Math.max(r.lastSeq, evt.seq);
        if (r.notes.some((n) => n.noteId === id)) return;
        r.notes.push({
          noteId: id, requestId: r.requestId, author: who,
          body: p.body as SealedNeedToKnowRef, createdAtIso, seq: evt.seq,
        });
        r.notes.sort((a, b) => a.seq - b.seq);
        return;
      }
      default:
        return; // helper.availability is private/matcher-only; not folded into request views
    }
  }

  #applyState(requestId: string, to: RequestState, seq: number): void {
    const r = this.#byId.get(requestId); if (!r) return;
    const prev = this.#stateSeq.get(requestId) ?? -1;
    if (seq < prev) return; // later-seq wins
    r.state = to;
    this.#stateSeq.set(requestId, seq);
  }

  requestById(id: string): HelpRequestView | undefined { return this.#byId.get(id); }

  /** Public open requests from OTHERS a helper could take (not own, not terminal). */
  openRequests(): HelpRequestView[] {
    return [...this.#byId.values()].filter((r) => r.creator !== this.me && !TERMINAL.has(r.state));
  }

  /** My own requests, requests where I'm an accepted helper, and requests I offered on but
   *  am not (yet) accepted for — so an offer has a home from the moment it is made. */
  myActivity(): { requests: HelpRequestView[]; helping: HelpRequestView[]; offered: HelpRequestView[] } {
    const all = [...this.#byId.values()];
    const helping = all.filter((r) => r.creator !== this.me && r.acceptedHelpers.includes(this.me));
    return {
      requests: all.filter((r) => r.creator === this.me),
      helping,
      offered: all.filter((r) => r.creator !== this.me && !r.acceptedHelpers.includes(this.me) && r.offers.some((o) => o.helper === this.me)),
    };
  }

  /** Locally folded events still awaiting their echo (for the browser store). */
  pendingSnapshot(): PendingEntry[] { return [...this.#pending.values()]; }

  /** Re-fold persisted pending events on boot; entries older than PENDING_MAX_AGE_MS are dropped. */
  restorePending(entries: PendingEntry[], nowMs: number): void {
    for (const e of entries) {
      if (nowMs - e.atMs > PENDING_MAX_AGE_MS) continue;
      this.ingest({ seq: 0, json: e.json, pending: true, atMs: e.atMs });
    }
  }

  /** Highest note seq folded for a request (0 if none) — the cursor target on read. */
  latestNoteSeq(requestId: string): number {
    const r = this.#byId.get(requestId);
    if (!r || r.notes.length === 0) return 0;
    return r.notes.reduce((m, n) => Math.max(m, n.seq), 0);
  }

  /** True iff the request has a coordination note from someone else past `cursor`. */
  unreadFor(requestId: string, cursor: number, me: string): boolean {
    const r = this.#byId.get(requestId);
    if (!r) return false;
    return r.notes.some((n) => n.seq > cursor && n.author !== me);
  }
}

export function cellDistanceKm(a: string, b: string): number {
  const [la, lo] = cellToLatLng(a);
  const [lb, lob] = cellToLatLng(b);
  return greatCircleDistance([la, lo], [lb, lob], UNITS.km);
}
